import { useLayoutEffect, useState, type RefObject } from 'react';

type UseHasVerticalOverflowStrategy = 'element' | 'nearestScrollParent';

const SCROLL_TOLERANCE = 1;
const INITIAL_STATE = { hasOverflow: false, isAtTop: true, isAtBottom: false };

const getScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null;

  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return node;
    }
    node = node.parentElement;
  }

  return null;
};

export const useHasVerticalOverflow = (
  elementRef: RefObject<HTMLElement | null>,
  strategy: UseHasVerticalOverflowStrategy,
  contentVersion: unknown
): { hasOverflow: boolean; isAtTop: boolean; isAtBottom: boolean } => {
  const [state, setState] = useState(INITIAL_STATE);

  useLayoutEffect(() => {
    const measure = (): void => {
      const root = elementRef.current;
      if (!root) {
        setState(INITIAL_STATE);
        return;
      }

      const target = strategy === 'element' ? root : getScrollableParent(root);

      if (!target) {
        setState(INITIAL_STATE);
        return;
      }

      const hasOverflow = target.scrollHeight > target.clientHeight;
      const isAtTop = target.scrollTop <= SCROLL_TOLERANCE;
      const isAtBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - SCROLL_TOLERANCE;

      setState(prev => {
        if (
          prev.hasOverflow === hasOverflow &&
          prev.isAtTop === isAtTop &&
          prev.isAtBottom === isAtBottom
        ) {
          return prev;
        }
        return { hasOverflow, isAtTop, isAtBottom };
      });
    };

    measure();

    const root = elementRef.current;
    if (!root) return;

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(root);

    let scrollTarget: HTMLElement | null = null;

    if (strategy === 'nearestScrollParent') {
      scrollTarget = getScrollableParent(root);
      if (scrollTarget && scrollTarget !== root) {
        resizeObserver.observe(scrollTarget);
      }
    } else {
      scrollTarget = root;
    }

    scrollTarget?.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);

    return (): void => {
      resizeObserver.disconnect();
      scrollTarget?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [elementRef, strategy, contentVersion]);

  return state;
};
